import { Component } from '@theme/component';

function publishFatherhoodEvent(eventName, detail) {
  const payload = { event: eventName, ...detail };

  document.dispatchEvent(
    new CustomEvent('fatherhood:analytics', {
      bubbles: true,
      composed: true,
      detail: payload,
    })
  );

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, detail);
  } else if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push(payload);
  }
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[data-fatherhood-amazon-cta]');
    if (link instanceof HTMLAnchorElement) {
      publishFatherhoodEvent('amazon_cta_click', {
        link_url: link.href,
        link_text: link.textContent?.trim() ?? '',
        link_location: link.dataset.fatherhoodAmazonCta ?? 'unknown',
      });
      return;
    }

    const guideLink = event.target.closest('a[data-fatherhood-guide-download]');
    if (!(guideLink instanceof HTMLAnchorElement)) return;

    publishFatherhoodEvent('file_download', {
      file_name: '7-conversations-fatherhood-guide.pdf',
      link_url: guideLink.href,
      link_text: guideLink.textContent?.trim() ?? '',
    });
  },
  { capture: true }
);

/**
 * Controls the compact mobile navigation for the Fatherhood header.
 * @extends {Component}
 */
class FatherhoodHeaderComponent extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    document.addEventListener('keydown', this.#handleKeydown, { signal: this.#controller.signal });
    this.#setOpen(false);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setOpen(false);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  /** @param {MouseEvent} event */
  toggleMenu(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    this.#setOpen(event.target.getAttribute('aria-expanded') !== 'true');
  }

  closeMenu() {
    this.#setOpen(false);
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.#setOpen(false);
    if (this.refs.menuToggle instanceof HTMLButtonElement) this.refs.menuToggle.focus();
  };

  /** @param {boolean} isOpen */
  #setOpen(isOpen) {
    const toggle = this.refs.menuToggle;
    const menu = this.refs.mobileMenu;

    if (!(toggle instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;

    toggle.setAttribute('aria-expanded', String(isOpen));
    menu.setAttribute('aria-hidden', String(!isOpen));
    menu.classList.toggle('is-open', isOpen);
    menu.inert = !isOpen;
  }
}

/**
 * Swaps the primary journal image from accessible thumbnail buttons.
 * @extends {Component}
 */
class FatherhoodGalleryComponent extends Component {
  /** @param {MouseEvent} event */
  selectImage(event) {
    const thumbnail = event.target;
    const mainImage = this.refs.mainImage;
    const thumbnails = this.refs.thumbnail;

    if (!(thumbnail instanceof HTMLButtonElement) || !(mainImage instanceof HTMLImageElement)) return;

    const src = thumbnail.dataset.fullSrc;
    if (!src) return;

    mainImage.src = src;
    mainImage.srcset = thumbnail.dataset.fullSrcset ?? '';
    mainImage.alt = thumbnail.dataset.alt ?? '';

    if (Array.isArray(thumbnails)) {
      for (const item of thumbnails) {
        if (!(item instanceof HTMLButtonElement)) continue;
        const isSelected = item === thumbnail;
        item.classList.toggle('is-active', isSelected);
        item.setAttribute('aria-pressed', String(isSelected));
      }
    }
  }
}

/**
 * Keeps FAQ disclosure state and ARIA relationships in sync.
 * @extends {Component}
 */
class FatherhoodFaqComponent extends Component {
  /** @param {MouseEvent} event */
  toggleItem(event) {
    const toggle = event.target;
    if (!(toggle instanceof HTMLButtonElement)) return;

    const contentId = toggle.getAttribute('aria-controls');
    if (!contentId) return;

    const content = this.querySelector(`#${CSS.escape(contentId)}`);
    if (!(content instanceof HTMLElement)) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isExpanded));
    content.setAttribute('aria-hidden', String(isExpanded));
  }
}

/**
 * Reveals marked content once without leaving observers attached.
 * @extends {Component}
 */
class FatherhoodRevealComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }

  #observe() {
    this.#observer?.disconnect();

    const items = this.refs.revealItem;
    const revealItems = Array.isArray(items) ? items : items instanceof Element ? [items] : [];
    if (revealItems.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      for (const item of revealItems) item.classList.add('fm-is-visible');
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('fm-is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    for (const item of revealItems) this.#observer.observe(item);
  }
}

/**
 * Emits privacy-safe storefront events that an installed analytics tool can consume.
 * No personal data is included in either event.
 * @extends {Component}
 */
class FatherhoodTrackingComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (!(this.querySelector('[data-fatherhood-signup-success]') instanceof HTMLElement)) return;

    const trackingKey = `fatherhood:free-guide-signup:${this.dataset.formId ?? window.location.pathname}`;
    try {
      if (window.sessionStorage.getItem(trackingKey) === 'true') return;
      window.sessionStorage.setItem(trackingKey, 'true');
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }

    publishFatherhoodEvent('generate_lead', {
      lead_source: 'free_guide',
      form_id: this.dataset.formId ?? '',
    });
  }
}

if (!customElements.get('fatherhood-header-component')) {
  customElements.define('fatherhood-header-component', FatherhoodHeaderComponent);
}

if (!customElements.get('fatherhood-gallery-component')) {
  customElements.define('fatherhood-gallery-component', FatherhoodGalleryComponent);
}

if (!customElements.get('fatherhood-faq-component')) {
  customElements.define('fatherhood-faq-component', FatherhoodFaqComponent);
}

if (!customElements.get('fatherhood-reveal-component')) {
  customElements.define('fatherhood-reveal-component', FatherhoodRevealComponent);
}

if (!customElements.get('fatherhood-tracking-component')) {
  customElements.define('fatherhood-tracking-component', FatherhoodTrackingComponent);
}
